import { Injectable } from '@angular/core';
import { Observable, of, combineLatest, BehaviorSubject } from 'rxjs';
import { filter, map, shareReplay, take, tap } from 'rxjs/operators';
import { UserService } from '@services/user.service';

export interface ProfileUserInfo {
  picture?: string | null;
  given_name?: string | null;
  family_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  email_verified?: boolean | null;
  [key: string]: any;
}

export interface ProfileDetails {
  userId: string;
  roleName: string;
  userInfo: ProfileUserInfo;
}

@Injectable({
  providedIn: 'root',
})
export class ProfileDetailsService {
  private cache$ = new BehaviorSubject<ProfileDetails | null>(null);

  constructor(private userService: UserService) {}

  getProfileDetails$(forceRefresh = false): Observable<ProfileDetails> {
    if (!forceRefresh && this.cache$.value) {
      return of(this.cache$.value);
    }

    return combineLatest([
      this.userService.getRoleDetails(forceRefresh),
      this.userService
        .getCognitoUserAttributes$(forceRefresh)
        .pipe(filter(attrs => attrs !== null)),
    ]).pipe(
      take(1),
      map(([role, cognitoUserAttribute]) =>
        this.mergeRoleAndCognito(role, cognitoUserAttribute)
      ),
      tap(details => this.cache$.next(details)),
      shareReplay(1)
    );
  }

  updateProfileDetailsFromEdit(updatedData: Partial<ProfileUserInfo>): void {
    const current = this.cache$.value;
    if (!current) return;

    const merged = { ...current.userInfo, ...updatedData };

    if (
      updatedData.hasOwnProperty('email') &&
      this.normalizeEmail(updatedData.email) !==
        this.normalizeEmail(current.userInfo.email)
    ) {
      merged.email_verified = false;
    }

    this.cache$.next({
      ...current,
      userInfo: merged,
    });
  }

  setVerification(contactType: 'email', verified: boolean): void {
    const current = this.cache$.value;
    if (!current) return;
    const key = contactType + '_verified';
    this.cache$.next({
      ...current,
      userInfo: { ...current.userInfo, [key]: verified },
    });
  }

  clearCache(): void {
    this.cache$.next(null);
  }

  private mergeRoleAndCognito(
    role: any,
    cognitoUserAttribute: any
  ): ProfileDetails {
    const roleName = role?.name || '';
    const userInfo = { ...(role?.data || {}) } as ProfileUserInfo;
    if (typeof role?.mfa_set === 'boolean') {
      userInfo.mfa_set = role.mfa_set;
    }
    userInfo.email_verified = false;
    if (cognitoUserAttribute) {
      const roleEmail = this.normalizeEmail(userInfo.email);
      const cognitoEmail = this.normalizeEmail(cognitoUserAttribute?.email);
      const emailsMatch = roleEmail === cognitoEmail;
      const noRoleEmail = roleEmail === '';
      if (typeof cognitoUserAttribute?.email_verified === 'boolean') {
        if (emailsMatch || noRoleEmail) {
          userInfo.email_verified = cognitoUserAttribute.email_verified;
        }
      }
    }

    return {
      userId: role?.user_id ?? '',
      roleName,
      userInfo,
    };
  }

  private normalizeEmail(email: string | null | undefined): string {
    return (email || '').toLowerCase().trim();
  }
}
